import { useEffect, useMemo, useRef, useState } from 'react';
import type { ExportDestinationContent, ExportStructureNode, SampleBatch, FormatWarning } from '@capture/shared';
import {
  renderJsonPreview,
  renderXmlPreview,
  renderCsvPreview,
  renderFixedWidthPreview,
  checkFixedWidthTruncation,
  checkXmlTagNames,
  checkCsvDelimiterConflicts,
} from '@capture/shared';
import { fetchSampleDocuments } from './api/sample-documents-client';
import type { ExportColumn } from '../../hooks/useExportFormats';
import { buildSpecByColumn } from './spec-by-column';
import styles from './export-composer.module.css';

type Mapping = ExportDestinationContent['mappings'][number];
type Format = ExportDestinationContent['format'];

interface CsvConfig {
  delimiter: string;
  quote: string;
}

interface ExportPreviewProps {
  mappings: Mapping[];
  format: Format;
  structure?: ExportStructureNode;
  sampleData: SampleBatch;
  /** Optional column widths for fixed-width format (targetColumn → width in chars). */
  fixedWidths?: Record<string, number>;
  /** Optional CSV delimiter/quote config for conflict detection. */
  csvConfig?: CsvConfig;
  /** When provided, renders the "Load from batch" toggle. */
  jobTypeId?: string;
  /**
   * The selected export format's columns (carrying their typed `format` spec).
   * When provided, the preview applies column-level format specs via the same
   * `resolveEffectiveColumns` path the worker uses, so preview matches export
   * output. Omitted for formatless destinations (legacy value-shape fallback).
   * (EX-2/F-053.)
   */
  columns?: ExportColumn[];
}

export function ExportPreview({
  mappings,
  format,
  structure,
  sampleData,
  fixedWidths = {},
  csvConfig,
  jobTypeId,
  columns,
}: ExportPreviewProps) {
  const [useBatch, setUseBatch] = useState(false);
  // null = not yet fetched, 'empty' = fetch returned no docs, array = loaded records
  const [batchRecords, setBatchRecords] = useState<SampleBatch['records'] | 'empty' | null>(null);

  // Keep a stable ref so the effect can read the latest special fields without
  // adding sampleData.special (an object) to the dep array — prevents infinite re-fetches
  // when sampleData is regenerated each render.
  const sampleDataRef = useRef(sampleData);
  sampleDataRef.current = sampleData;

  useEffect(() => {
    if (!useBatch || !jobTypeId) {
      setBatchRecords(null);
      return;
    }

    let cancelled = false;
    fetchSampleDocuments(jobTypeId).then((resp) => {
      if (cancelled) return;
      setBatchRecords(
        resp.documents.length === 0
          ? 'empty'
          : resp.documents.map((d) => d.fieldValues as Record<string, string | number | boolean>),
      );
    }).catch(() => {
      if (!cancelled) setBatchRecords('empty');
    });

    return () => { cancelled = true; };
  }, [useBatch, jobTypeId]);

  const effectiveSample: SampleBatch = useMemo(() => {
    if (useBatch && Array.isArray(batchRecords)) {
      return { records: batchRecords, special: sampleDataRef.current.special };
    }
    return sampleData;
  }, [useBatch, batchRecords, sampleData]);

  // Column → effective format spec, built via the same shared resolver the
  // worker uses (resolveEffectiveColumns) so preview matches export output.
  const specByColumn = useMemo(
    () => buildSpecByColumn(columns, mappings),
    [columns, mappings],
  );

  const previewText = useMemo(() => {
    switch (format) {
      case 'json':
        return renderJsonPreview(mappings, effectiveSample, structure, specByColumn);
      case 'xml':
        return renderXmlPreview(mappings, effectiveSample, structure, specByColumn);
      case 'csv':
        return renderCsvPreview(mappings, effectiveSample, specByColumn);
      case 'fixed-width':
        return renderFixedWidthPreview(mappings, effectiveSample, fixedWidths, specByColumn);
      default:
        return '';
    }
  }, [mappings, format, structure, effectiveSample, fixedWidths, specByColumn]);

  const warnings: FormatWarning[] = useMemo(() => {
    // Validators accept string | null; SampleBatch uses string | number | boolean.
    // The cast is safe: all validator checks use ?? '' or .includes() on stringified paths.
    const sampleValues = effectiveSample.records as unknown as Array<Record<string, string | null>>;
    switch (format) {
      case 'fixed-width':
        return checkFixedWidthTruncation(mappings, sampleValues, fixedWidths);
      case 'xml':
        return checkXmlTagNames(structure);
      case 'csv':
        if (csvConfig) {
          return checkCsvDelimiterConflicts(mappings, sampleValues, csvConfig);
        }
        return [];
      default:
        return [];
    }
  }, [format, mappings, effectiveSample, fixedWidths, structure, csvConfig]);

  const formatLabel = format.toUpperCase();
  const recordCount = effectiveSample.records.length;

  return (
    <div className={styles.previewPanel}>
      <div className={styles.previewHeader}>
        <span className={styles.previewTitle}>
          Live Preview ({formatLabel})
        </span>

        {jobTypeId && (
          <label className={styles.previewBatchToggle}>
            <input
              type="checkbox"
              checked={useBatch}
              onChange={(e) => setUseBatch(e.target.checked)}
              aria-label="Load from batch"
            />
            {' '}Load from batch
          </label>
        )}
      </div>

      {useBatch && batchRecords === 'empty' && (
        <p className={styles.previewInfo}>
          No sample batch attached — using synthetic preview. Add samples in the Sample Data tab.
        </p>
      )}

      {warnings.length > 0 && (
        <ul
          className={styles.previewWarnings}
          data-testid="preview-warnings"
        >
          {warnings.map((w, i) => (
            <li key={`${w.code}-${w.fieldName ?? i}`} className={styles.previewWarningItem}>
              <span className={styles.previewWarningBadge}>
                {w.severity === 'error' ? 'ERROR' : 'WARN'}
              </span>
              {w.message}
            </li>
          ))}
        </ul>
      )}

      <pre className={styles.previewPre} data-testid="export-preview-pre">
        <code>{previewText}</code>
      </pre>

      <div className={styles.previewStatus}>
        <span className={styles.previewValid}>
          ✓ Valid {formatLabel} · {recordCount} sample record{recordCount !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
}
